import { StatusBar, Modal, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './GlobalItemTypeEditModal.module.scss';

import chefPlating from 'src/assets/images/chef-plating.png';
import { IMenuItem, TMenuItemType } from 'src/types/menu.types';
import { GlobalItemTypeForm } from '../CreateGlobalItemWizard/GlobalItemTypeForm/GlobalItemTypeForm';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { IGlobalItem } from '../CreateGlobalItemWizard/types';
import { updateGlobalItemDetails } from 'src/api/updateGlobalItemDetails';
import { IUpdateDetailsRequestData } from 'src/api/updateGlobalItemDetails/types';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { FormVariant } from 'src/types/menuItemForm.types';

interface IGlobalItemTypeEditModal {
  onModalClose: () => void;
  isModalOpen: boolean;
  setGlobalItem?: (item: Partial<IGlobalItem>) => void;
  currentItem: IMenuItem;
}

const GlobalItemTypeEditModal = ({
  isModalOpen,
  onModalClose,
  setGlobalItem,
  currentItem,
}: IGlobalItemTypeEditModal) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onEdit = async (data: Partial<IGlobalItem>, isDirty: boolean) => {
    clearError();
    try {
      if (isDirty) {
        setIsSubmitting(true);

        const requestData: IUpdateDetailsRequestData = {
          type: data.type as Exclude<TMenuItemType, 'BUNDLE'>,
        };
        await updateGlobalItemDetails(currentItem.itemId, {
          data: JSON.stringify(requestData),
        });

        const queryKey = [QueryKeys.item, currentItem.itemId];
        await queryClient.invalidateQueries(queryKey);

        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: `You have successfully updated type of ${currentItem.name}.`,
        });
      }
      onModalClose();
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst updating type of a product',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={isModalOpen} closeModal={onModalClose} image={chefPlating} imageAlt="chef">
      <>
        <GlobalItemTypeForm
          variant={FormVariant.UPDATE}
          isSubmitting={isSubmitting}
          onCancel={onModalClose}
          defaultType={currentItem.type}
          onSubmitItem={onEdit}
          setGlobalItem={setGlobalItem}
        />
        <div className={styles['error-banner']}>
          {errorMessage && <StatusBar message={errorMessage} variant="error" />}
        </div>
      </>
    </Modal>
  );
};

export default GlobalItemTypeEditModal;
